import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { animate, motion, useInView } from "framer-motion";
import { ArrowDown, ArrowRight, Code2, HeartPulse } from "lucide-react";
import { Reveal, SectionHeading, EASE } from "./Reveal";

const Counter = ({ to, prefix = "", suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, { duration: 1.8, ease: EASE, onUpdate: (v) => setVal(Math.round(v)) });
    return () => controls.stop();
  }, [inView, to]);
  return <span ref={ref} data-testid={`counter-${to}${suffix.replace(/\W/g, "")}`}>{prefix}{val}{suffix}</span>;
};

const STATS = [
  { value: <Counter to={30} prefix="$" suffix="K+" />, range: "$15K–$30K+", label: "Potential Annual Savings vs. In-Person Scribes" },
  { value: <Counter to={15} suffix="%" />, range: "10–15%", label: "More Patients Seen Per Session in Published Studies" },
];

const Revenue = () => (
  <section className="relative overflow-hidden bg-gradient-to-br from-brand-navy-deep via-brand-navy to-[#1E4C8F] dark:from-[#05081C] dark:via-[#0A1030] dark:to-brand-navy-deep py-24 md:py-32 grain" data-testid="revenue-section">
    <div className="absolute top-0 left-1/3 w-[480px] h-[480px] rounded-full bg-brand-sky/20 blur-3xl" />
    <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 items-center">
      <div>
        {STATS.map((s, i) => (
          <div key={s.label}>
            <Reveal delay={i * 0.1}>
              <div className="rounded-3xl bg-white/[0.07] backdrop-blur-xl border border-white/15 p-7 flex items-center gap-6 hover:bg-white/[0.12] transition-colors duration-300" data-testid={`revenue-stat-${i}`}>
                <div className="shrink-0">
                  <p className="font-display text-4xl md:text-5xl font-bold text-white tracking-tight">{s.value}</p>
                  <p className="text-[11px] font-bold text-brand-sky tracking-widest mt-1">{s.range}</p>
                </div>
                <div className="h-12 w-px bg-white/15" />
                <p className="font-display text-base md:text-lg font-semibold text-white/90">{s.label}</p>
              </div>
            </Reveal>
            {i < STATS.length - 1 && (
              <Reveal delay={i * 0.1 + 0.05}>
                <div className="flex justify-center py-2">
                  <ArrowDown size={18} className="text-brand-sky" />
                </div>
              </Reveal>
            )}
          </div>
        ))}
        <Reveal delay={0.35}>
          <div className="mt-6 flex flex-wrap gap-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-brand-sky/15 border border-brand-sky/30 text-brand-sky text-xs font-bold px-4 py-2">
              <Code2 size={13} /> Better Coding
            </span>
            <ArrowDown size={14} className="text-brand-sky self-center rotate-[-90deg]" />
            <span className="inline-flex items-center gap-2 rounded-full bg-brand-sky/15 border border-brand-sky/30 text-brand-sky text-xs font-bold px-4 py-2">
              <HeartPulse size={13} /> Reduced Burnout
            </span>
          </div>
        </Reveal>
      </div>
      <div>
        <SectionHeading
          id="revenue"
          overline="The Business Case"
          title={
            <span className="text-white">
              Scribes don't just save time. <span className="text-brand-sky">They can create capacity for growth.</span>
            </span>
          }
        />
        <Reveal delay={0.2}>
          <div className="mt-6 space-y-5 text-base md:text-lg leading-relaxed text-white/70">
            <p>
              Virtual scribes can reduce staffing costs compared with in-person scribes while supporting productivity
              and documentation quality — a rare case where the more efficient option is also the more affordable one.
            </p>
            <p>
              Published studies have associated scribe use with increased physician productivity, additional patient
              capacity, and improved documentation supporting coding. More complete clinical documentation may better
              support the level of service actually provided.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-9 flex flex-wrap items-center gap-5">
            <Link
              to="/resources"
              data-testid="revenue-read-research-button"
              className="group inline-flex items-center gap-2.5 rounded-full bg-brand-sky text-brand-ink font-bold px-8 py-4 text-sm hover:shadow-[0_20px_45px_-14px_rgba(79,195,232,0.5)] hover:-translate-y-0.5 transition-[transform,box-shadow] duration-300"
            >
              Read the Research
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
            <div className="flex gap-2 text-[11px] font-bold tracking-widest uppercase text-white/40">
              {["AMA", "ScribeAmerica", "Scribeology", "UCSF"].map((s) => (
                <span key={s} className="rounded-full border border-white/15 px-3 py-1.5">{s}</span>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
export default Revenue;
